import { useAsPrimaryPointerEvent } from './useAsPrimaryPointerEvent';
import { usePointerEvents } from './usePointerEvents';
import type { MouseEventHandler, PointerEventHandler } from 'react';

/**
 * Returns either a mouse event handler or a pointer event handler, depending on the value of usePointerEvents.
 * The expected usage is for the caller to deconstruct the array and pass the callbacks to HTML elements as onMouse* and onPointer* handlers.
 * Ex:
 * ```ts
 * const [onMouseDown, onPointerDown] = useMouseOrPrimaryPointerEvent(callbackForMouseDown);
 * return <div onMouseDown={onMouseDown} onPointerDown={onPointerDown} />
 * ```
 */
export function useMouseOrPrimaryPointerEvent<T>(
    mouseEventHandler: MouseEventHandler<T>
): [MouseEventHandler<T>?, PointerEventHandler<T>?] {
    const usingPointerEvents = usePointerEvents();
    const pointerEventHandler = useAsPrimaryPointerEvent(mouseEventHandler);
    return [
        usingPointerEvents ? undefined : mouseEventHandler,
        usingPointerEvents ? pointerEventHandler : undefined,
    ];
}
